import styled from 'styled-components'

import { media } from 'styles/media'

import ButtonRouterLink from 'components/display/ButtonRouterLink'
import Alert from 'components/display/Alert'
import CalendarBookingDetailed from 'components/icons/CalendarBookingsDetailed'
import ClockDetailed from 'components/icons/ClockDetailed'
import NiceFindDetailed from 'components/icons/NiceFindDetailed'
import ClickDetailed from 'components/icons/ClickDetailed'
import StarrySky from 'components/icons/StarrySky'

import {
  useGetIncompleteBookingsQuery,
  useGetListingAvailabilityAlertsQuery,
} from 'hooks/useQueries'
import formatDateRange from 'helpers/formatDateRange'
import DiscountDescription from '../DiscountDescription'

// It is not deemed worthwhile alerting the user for discounts less than this percentage
export const discountPercentageThresholdForAlert = 15 as const

const SpacedMessage = styled.span`
  ${media.md`
    margin-right: 16px;
  `}
`

const AlertContentWrapper = styled.div`
  ${media.md`
    display: flex;
    align-items: center;
  `}
`

const ButtonLink = styled(ButtonRouterLink)`
  flex-shrink: 0;
  padding: 12px;
  margin-top: 16px;

  ${media.md`
    margin-left: auto;
    margin-top: 0;
  `}
`

type Props = {
  listingId: string
  marketName: string
  now?: Date
}

const ListingAlert = ({ listingId, marketName, now = new Date() }: Props) => {
  const { data: incompleteBookingsData } = useGetIncompleteBookingsQuery()

  const { data: listingAvailabilityAlertsData, loading } =
    useGetListingAvailabilityAlertsQuery({
      variables: {
        listingId,
      },
    })

  const incompleteBooking =
    incompleteBookingsData?.currentUser?.incompleteBookings?.filter(
      ({ listing }) => listing?.id === listingId,
    )?.[0]

  const thisWeekend =
    listingAvailabilityAlertsData?.listing?.weekendAvailabilities?.[0]
  const nextWeekend =
    listingAvailabilityAlertsData?.listing?.weekendAvailabilities?.[1]
  const promotionalDiscounts =
    listingAvailabilityAlertsData?.listing?.promotionalDiscounts
  const popular = listingAvailabilityAlertsData?.listing?.popular

  const alertableDiscounts = promotionalDiscounts
    ?.filter(
      ({ percentage }) => percentage >= discountPercentageThresholdForAlert,
    )
    ?.filter(({ endDate }) => new Date(endDate) > now)

  if (incompleteBooking) {
    const { id: bookingId, startDate, endDate } = incompleteBooking
    const dateRange = formatDateRange(startDate, endDate, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    })
    return (
      <Alert $darkAppearance>
        <ClickDetailed />
        <AlertContentWrapper>
          <SpacedMessage>
            <strong>You’re almost there!</strong> Complete your booking for{' '}
            {dateRange} before someone else snaps it up.
          </SpacedMessage>

          <ButtonLink
            to={`/my/bookings/${bookingId}/payment`}
            uppercase={false}
            ghost
          >
            Let’s go
          </ButtonLink>
        </AlertContentWrapper>
      </Alert>
    )
  }

  if (alertableDiscounts?.length > 0) {
    const [discount] = promotionalDiscounts

    return (
      <Alert>
        <StarrySky />
        <span>
          <DiscountDescription discount={discount} />
        </span>
      </Alert>
    )
  }

  if (thisWeekend?.available) {
    const { startDate, endDate } = thisWeekend
    const dateRange = formatDateRange(startDate, endDate)
    return (
      <Alert>
        <ClockDetailed />
        <span>
          <strong>Get in quick!</strong> This escape is available this weekend (
          {dateRange}).
        </span>
      </Alert>
    )
  }

  if (nextWeekend?.available) {
    const { startDate, endDate } = nextWeekend
    const dateRange = formatDateRange(startDate, endDate)
    return (
      <Alert>
        <CalendarBookingDetailed />
        <span>
          <strong>Get in quick!</strong> This escape is available next weekend (
          {dateRange}).
        </span>
      </Alert>
    )
  }

  if (popular) {
    return (
      <Alert>
        <NiceFindDetailed />
        <span>
          <strong>Nice find!</strong> This is one of the most sought-after
          escapes in {marketName}.
        </span>
      </Alert>
    )
  }

  if (loading) {
    return <div data-testid="loading" />
  }

  return null
}

export default ListingAlert
